import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router'

// Components
import ArchivedUsersModal from './ArchivedUsersModal'
import EvaluateRequestModal from './evaluations/EvaluateRequestModal'
import SuggestAssessorsModal from './evaluations/SuggestAssessorsModal'
import CompanySetupChecklistModal from './settings/CompanySetupChecklistModal'

// Utils
import { useAppDispatch, useAppState } from '../context/AppContext'
import getMembersToBeDeletedService from '../context/services/membersDeleteQueues/getMembersToBeDeleted.service'
import getMemberOutgoingEvaluationsService from '../context/services/evaluations/getMemberOutgoingEvaluations.service'

// Types
import { RequestType } from '../common/models/Request'
import { IEvaluationPure } from '../common/models/Evaluation'

// Renders the modal of the request in `requestModal` app state, once, in the app
// shell. Both the dashboard request list and the activity panel open a modal by
// dispatching `updateRequestModal`, so it opens on whatever page the user is on
// instead of routing to a dashboard first. It loads what each modal needs on open,
// and closes when the route changes: a modal belongs to the page it opened on.
const RequestModals: React.FC = () => {
  const appDispatch = useAppDispatch()
  const { pathname } = useLocation()
  const { requestModal: request, company, userAuthToken } = useAppState()

  const [memberIdsToBeDeleted, setMemberIdsToBeDeleted] = useState<string[]>()
  const [evaluations, setEvaluations] = useState<IEvaluationPure[]>()

  const closeModal = () =>
    appDispatch({ type: 'updateRequestModal', payload: undefined })

  useEffect(closeModal, [pathname])

  useEffect(() => {
    setMemberIdsToBeDeleted(undefined)
    setEvaluations(undefined)
    if (!request || !userAuthToken) return

    if (request.actionType === RequestType.DELETE_ARCHIVED_MEMBERS) {
      getMembersToBeDeletedService(userAuthToken)
        .then(setMemberIdsToBeDeleted)
        .catch(() => setMemberIdsToBeDeleted([]))
    } else if (request.actionType === RequestType.EVALUATE) {
      // Nothing left to evaluate means nothing to show: close rather than leave an
      // invisible modal open that would swallow the next click on this request
      getMemberOutgoingEvaluationsService(request.termNumber, userAuthToken)
        .then((res) => (res.length ? setEvaluations(res) : closeModal()))
        .catch(closeModal)
    }
  }, [request, userAuthToken])

  switch (request?.actionType) {
    case RequestType.DELETE_ARCHIVED_MEMBERS:
      return memberIdsToBeDeleted ? (
        <ArchivedUsersModal
          closeModal={closeModal}
          memberIdsToBeDeleted={memberIdsToBeDeleted}
        />
      ) : null
    case RequestType.EVALUATE:
      return evaluations ? (
        <EvaluateRequestModal
          evaluationName={
            company?.termsInformation.terms.find(
              (term) => term.termNumber === request.termNumber
            )?.name || ''
          }
          evaluations={evaluations}
          closeModal={closeModal}
        />
      ) : null
    case RequestType.SPECIFY_CLOSE_MEMBERS:
      return (
        <SuggestAssessorsModal
          termNumber={request.termNumber}
          closeModal={closeModal}
        />
      )
    case RequestType.COMPLETE_COMPANY_PROFILE:
      return <CompanySetupChecklistModal closeModal={closeModal} />
    default:
      return null
  }
}

export default RequestModals
